/**
 * The words the Find sheet offers under "Words".
 *
 * Search now reads every controlled value on a profile, so the vocabularies are the honest
 * source for a suggestion: each of these words is one the index actually holds, which is the
 * difference between a suggestion and a guess. They are the product labels rather than the
 * stored slugs, because the label is what the profile renders and what somebody would type.
 *
 * `LIMITS` is deliberately not here. It is the one public list search never matches, so
 * offering "COF" as a suggestion would hand the visitor a word that always returns nothing.
 */

import {
  AESTHETIC,
  AT_FIRST_GLANCE,
  BODY_TYPE,
  BUST,
  CONSIDERS,
  DRESS_SIZE,
  ESCORT_FOR,
  ETHNICITY,
  EYES,
  GENDER,
  HAIR_COLOUR,
  HAIR_STYLE,
  LANGUAGES,
  SEXUALITY,
  type FieldVocabulary,
} from '@shared/profile-fields';

/**
 * Ordered, because the first eight are what an unfiltered sheet shows: what somebody offers and
 * what they look like at a glance come before the size of a dress.
 */
const POOL: FieldVocabulary[] = [
  CONSIDERS,
  AT_FIRST_GLANCE,
  AESTHETIC,
  BODY_TYPE,
  ETHNICITY,
  LANGUAGES,
  HAIR_COLOUR,
  EYES,
  GENDER,
  SEXUALITY,
  ESCORT_FOR,
  HAIR_STYLE,
  BUST,
  DRESS_SIZE,
];

/** How many are offered at once. Eight is two thumb rows on a 320px sheet. */
export const MAX_SUGGESTIONS = 8;

/**
 * What an untouched box offers, chosen rather than taken off the top of the pool.
 *
 * The pool's own order leads with the whole `considers` list, which puts "CIMWS" in front of a
 * visitor who has typed nothing — an abbreviation is a poor first word to meet, and eight words
 * from one vocabulary reads as the only thing that can be searched. These eight are one from
 * each of the axes somebody actually decides on: a look, a body, a colour, a background, a
 * language, and what is on offer.
 *
 * Every entry must exist in `SUGGESTIONS`; the unit test is what holds that true when a
 * vocabulary changes underneath it.
 */
const LEAD = ['Cute', 'Curvy', 'Petite', 'Blonde', 'Thai', 'English', 'BDSM', 'Foot Fetish'];

/**
 * Every label once. "Fit" is both a first glance and an aesthetic, and "Black" is hair, eyes and
 * ethnicity at the same time: the search does not care which vocabulary a word came from, so
 * neither does the list.
 */
export const SUGGESTIONS: string[] = (() => {
  const seen = new Map<string, string>();
  for (const vocabulary of POOL) {
    for (const option of vocabulary.options) {
      const key = option.label.toLowerCase();
      if (!seen.has(key)) seen.set(key, option.label);
    }
  }
  return [...seen.values()];
})();

/** The part of the query being typed: everything after the last space. */
const fragment = (query: string) => query.slice(query.lastIndexOf(' ') + 1).trim().toLowerCase();

/**
 * What to offer for what has been typed so far.
 *
 * An empty box gets the head of the list rather than nothing, because a visitor who does not
 * know what this site calls things needs an example before they need a filter. A word already
 * in the query is dropped: suggesting it a second time would only add it twice.
 */
export function suggestFor(query: string, limit = MAX_SUGGESTIONS): string[] {
  const typed = fragment(query);
  const already = new Set(
    query
      .toLowerCase()
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, typed ? -1 : undefined),
  );

  // Nothing typed and nothing chosen yet: the curated eight, which are examples rather than a
  // filtered list. Once a word is in the box the pool takes over, because the visitor is now
  // narrowing rather than being introduced.
  const pool = !typed && !already.size ? LEAD : SUGGESTIONS;

  return pool.filter((label) => {
    const value = label.toLowerCase();
    if (already.has(value)) return false;
    return typed ? value.includes(typed) : true;
  }).slice(0, limit);
}

/**
 * The query once a suggestion is taken: it completes the word being typed rather than replacing
 * the whole box, so "thai " then "BDSM" narrows to both. The trailing space is what lets the
 * next suggestion start from an empty fragment.
 */
export function applySuggestion(query: string, label: string): string {
  const head = query.slice(0, query.lastIndexOf(' ') + 1);
  return `${head}${label} `;
}
